import { useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';

export interface QuantityStepperProps {
  /** Current quantity (controlled). */
  value: number;
  /**
   * Called with the requested integer quantity. The PARENT owns the
   * clamping / removal policy: the −/+ buttons emit `value ± 1` UNCLAMPED so
   * each caller keeps its existing behaviour (the product modal floors at 1,
   * the cart lets 0 remove the line). The keyboard field, in contrast, always
   * clamps the committed value to `[min, max]` so a user can never type a line
   * away by mistake — removal stays an explicit −/trash action.
   */
  onChange: (next: number) => void;
  /** Lower bound applied to the TYPED value on commit (default 1). */
  min?: number;
  /** Optional upper bound applied to the TYPED value on commit. */
  max?: number;
  /** Visual style: `bordered` = product modal, `pill` = cart line. */
  variant?: 'bordered' | 'pill';
  decreaseLabel: string;
  increaseLabel: string;
  inputLabel: string;
  /** Extra classes merged onto the container (e.g. layout margins). */
  className?: string;
}

/**
 * Quantity stepper with a keyboard-editable value between the −/+ buttons.
 * Shared by the product detail modal and the cart line so the input UX
 * (integer-only, select-on-focus, commit on Enter/blur, clamp to [min,max])
 * lives in one place. Pro portal is FR-only → no RTL handling.
 */
export function QuantityStepper({
  value,
  onChange,
  min = 1,
  max,
  variant = 'bordered',
  decreaseLabel,
  increaseLabel,
  inputLabel,
  className,
}: QuantityStepperProps) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  // Escape cancels the edit: the flag makes the blur-triggered commit a no-op
  // so the typed draft is discarded instead of applied.
  const cancelledRef = useRef(false);

  const isPill = variant === 'pill';

  const commit = () => {
    setFocused(false);
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const parsed = parseInt(draft, 10);
    let next = Number.isNaN(parsed) ? min : parsed;
    if (next < min) next = min;
    if (max != null && next > max) next = max;
    if (next !== value) onChange(next);
  };

  const containerCls = [
    isPill
      ? 'inline-flex items-center gap-3 px-2 py-1 rounded-lg'
      : 'inline-flex items-center gap-2',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const btnCls = isPill
    ? 'w-4 h-4 flex items-center justify-center shrink-0'
    : 'w-9 h-9 rounded-lg border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center shrink-0';
  const iconCls = isPill ? 'w-3 h-3' : 'w-4 h-4';
  const inputCls = isPill
    ? 'w-9 text-center text-[12px] font-medium bg-transparent focus:outline-none p-0 border-0'
    : 'w-12 text-center font-medium bg-transparent focus:outline-none p-0 border-0';

  return (
    <div
      className={containerCls}
      style={isPill ? { background: 'var(--pro-primary-subtle)' } : undefined}
    >
      <button
        type="button"
        aria-label={decreaseLabel}
        onClick={() => onChange(value - 1)}
        className={btnCls}
        style={isPill ? { color: 'var(--pro-primary)' } : undefined}
      >
        <Minus className={iconCls} />
      </button>
      <input
        type="text"
        inputMode="numeric"
        aria-label={inputLabel}
        value={focused ? draft : String(value)}
        onFocus={(e) => {
          setDraft(String(value));
          setFocused(true);
          e.currentTarget.select();
        }}
        onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            // Cancel the edit locally; stopPropagation keeps the event away
            // from the product modal's window listener (which would close it).
            e.preventDefault();
            e.stopPropagation();
            cancelledRef.current = true;
            e.currentTarget.blur();
          }
        }}
        className={inputCls}
        style={isPill ? { color: 'var(--pro-text-dark)' } : undefined}
      />
      <button
        type="button"
        aria-label={increaseLabel}
        onClick={() => onChange(value + 1)}
        className={btnCls}
        style={isPill ? { color: 'var(--pro-primary)' } : undefined}
      >
        <Plus className={iconCls} />
      </button>
    </div>
  );
}
